<template>
  <!-- Connector scoping is keyed by the stable connector id; the comparison (EQ/NOTEQ/...) behaves
       like any other criterion. The autocomplete shows the connector name but stores its id. -->
  <UIInputSelect v-model="criteriaComparison" :size :data :disabled />
  <template v-if="isFieldVisible">
    <!-- EQ/NOTEQ scope by connector id, so pick from the list (shows name, stores id). Other
         comparisons (CONTAINS/NOTCONTAINS) are free-text matches on the id. -->
    <UIInputAutocomplete
      v-if="mode === 'autocomplete'"
      v-model="criteriaValue"
      :data="connectorsList"
      :size
      :disabled
      @ready="el => el.focus()"
    />
    <UIInputText v-if="mode === 'input'" v-model="criteriaValue" :size :disabled />
    <p v-if="isUnresolved" class="module-rules-criteria-connector__warning">
      This connector was deleted. Pick another connector or remove it.
    </p>
  </template>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, watch } from 'vue'

import {
  FilterComparison,
  DropdownListData,
  TransactionRuleCriteria,
  InputSize,
} from '@types'

import { getComparisonData } from '@/components/UI/Filter/components/utils/helpers'
import { handleCatchedError } from '@/helpers/common'

import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'

import { UIInputAutocomplete, UIInputSelect, UIInputText } from '@ui/fields'

type Props = {
  disabled?: boolean
}

defineProps<Props>()

const modelValue = defineModel<TransactionRuleCriteria['params'][number]>({
  required: true,
})

const connectorsStore = useLinkedDataConnectorsStore()

const size = inject<InputSize>('size')

const connectorsList = computed<DropdownListData[]>(() =>
  connectorsStore.getList.map(connector => ({
    value: connector.id,
    label: connector.name,
  })),
)

// In picker mode (EQ/NOTEQ) the value is a connector id; flag it when it no longer resolves (the
// connector was deleted). Free-text comparisons aren't connector ids, so they're never flagged.
const isUnresolved = computed(
  () =>
    mode.value === 'autocomplete' &&
    !!criteriaValue.value &&
    !connectorsStore.getById(criteriaValue.value),
)

const criteriaComparison = computed({
  get() {
    return modelValue.value.comparison
  },
  set(comparison) {
    modelValue.value = {
      ...modelValue.value,
      comparison,
    }
  },
})

const criteriaValue = computed({
  get() {
    return modelValue.value.value
  },
  set(value) {
    modelValue.value = {
      ...modelValue.value,
      value,
    }
  },
})

const data = computed(() =>
  getComparisonData().map(value => ({
    value,
    label: value,
  })),
)

// EQ/NOTEQ scope by connector id (autocomplete picker); other comparisons match the id as free text.
const mode = computed(() =>
  criteriaComparison.value &&
  [FilterComparison.EQ, FilterComparison.NOTEQ].includes(criteriaComparison.value)
    ? 'autocomplete'
    : 'input',
)

const isFieldVisible = computed(
  () =>
    criteriaComparison.value &&
    ![FilterComparison.EMPTY, FilterComparison.NOTEMPTY].includes(
      criteriaComparison.value,
    ),
)

watch(isFieldVisible, value => {
  criteriaValue.value = value ? '' : undefined
})

// Clear the value when switching between the id picker and the free-text field so a connector id
// isn't shown as raw text (and a free-text value isn't fed to the id picker). EQ↔NOTEQ stay in the
// picker, so the selected connector is preserved.
watch(mode, () => {
  criteriaValue.value = ''
})

onBeforeMount(async () => {
  // Default to EQ when no comparison is set yet (mirrors the other criteria editors, e.g.
  // CriteriaMethod) so the value field is visible immediately. The comparison stays unlocked, so
  // the user can switch to NOTEQ/CONTAINS afterwards.
  if (!criteriaComparison.value) {
    criteriaComparison.value = FilterComparison.EQ
  }

  if (!connectorsStore.initFlag) {
    try {
      await connectorsStore.fetch()
    } catch (e) {
      // `as string` is compile-time only; connectorsStore.fetch() rejects with an Error, so convert
      // to a real message string before logging (otherwise Faro receives a non-string object).
      handleCatchedError(e instanceof Error ? e.message : String(e))
    }
  }
})
</script>

<style>
.module-rules-criteria-connector__warning {
  @apply text-xs;
  @apply text-red-500 dark:text-red-300;
}
</style>
